"use client";

import Split from "@uiw/react-split";
import React, { type ReactNode, useId, useMemo, useRef, useState } from "react";
import { COOKIE_NEVER_EXPIRES, type CookieInfo } from "shared/SharedConstants";
import { getCookie } from "src/lib/AppSharedUtils";
import { setCookie } from "src/lib/hooks/ClientHooks";
import { useExtracted } from "#i18n";
import styles from "./SplitPane.module.scss";
import {
  parseSplitPaneLayout,
  type SplitPanePersistentState,
  type SplitPanePersistentStatePanel,
} from "./SplitPaneLayout";

export interface SplitPanePanelInfo {
  compact: boolean;
}

export interface SplitPaneHandle {
  resizePanel: (panelId: string, size: number) => void;
}

interface SplitPanePanelProps {
  children: ReactNode | ((info: SplitPanePanelInfo) => ReactNode);
  className?: string;
  id?: string;
  compactBreakpoint?: number;
  /** Renders the panel in a fixed theme, whatever the page's theme. */
  "data-theme"?: "dark";
}

export type SplitPaneDirection = "horizontal" | "vertical";

interface SplitPaneProps {
  children: ReactNode;
  className?: string;
  direction?: SplitPaneDirection;
  persistCookie?: CookieInfo;
}

interface SplitPaneContextValue {
  groupId: string;
  defaultState: SplitPanePersistentState | null;
  panelSizes: Record<string, number>;
  direction?: SplitPaneDirection;
}

const SplitPaneContext = React.createContext<SplitPaneContextValue | null>(null);

export const SplitPanePanel = (props: SplitPanePanelProps) => {
  const translateMessage = useExtracted();
  const { children, id, className = "", compactBreakpoint, "data-theme": theme } = props;

  const context = React.useContext(SplitPaneContext);

  const generatedId = useId();
  const resolvedId = id || generatedId;

  const panelSize = context?.panelSizes[resolvedId];

  const ref = useRef<HTMLDivElement>(null);

  const defaultState = context?.defaultState?.panels.find((panel) => panel.id === resolvedId);

  // Use panelSize from state if available, otherwise fall back to defaultState
  const sizeToUse = panelSize ?? defaultState?.size;

  // Determine if this panel should be in compact mode
  const compact = React.useMemo(() => {
    if (!compactBreakpoint || !ref.current) {
      return false;
    }
    const panelElement = ref.current;
    const panelHeight = panelElement?.clientHeight;
    return panelHeight !== undefined && panelHeight < compactBreakpoint;
  }, [compactBreakpoint, panelSize]);

  const panelInfo: SplitPanePanelInfo = {
    compact: compact,
  };

  return (
    <div
      ref={ref}
      id={resolvedId}
      data-panel-group={context?.groupId}
      data-panel-id={resolvedId}
      data-theme={theme}
      className={`${styles.panel} ${className}`}
      style={{
        [context?.direction === "horizontal" ? "width" : "height"]: sizeToUse
          ? `${sizeToUse}%`
          : undefined,
      }}
    >
      {typeof children === "function" ? children(panelInfo) : children}
    </div>
  );
};

const SplitPane = (props: SplitPaneProps) => {
  const translateMessage = useExtracted();
  const { children, className = "", direction = "horizontal", persistCookie } = props;

  const childrenArray = React.Children.toArray(children);

  const groupId = useId();

  // Load default layout from cookie once (works on both server and client)
  const defaultLayout = useMemo(
    () => parseSplitPaneLayout(persistCookie ? getCookie(persistCookie.name) : undefined),
    [persistCookie],
  );

  // Initialize panelSizes from defaultLayout (reuse the already-parsed layout)
  const [panelSizes, setPanelSizes] = useState<Record<string, number>>(() => {
    if (defaultLayout?.panels) {
      const sizes: Record<string, number> = {};
      defaultLayout.panels.forEach((panel: SplitPanePersistentStatePanel) => {
        if (panel.id) {
          sizes[panel.id] = panel.size;
        }
      });
      return sizes;
    }
    return {};
  });

  const handleDragEnd = (preSize: number, nextSize: number, paneNumber: number) => {
    setDragging(false);

    // Get container using data attributes
    const container = document.querySelector<HTMLElement>(`[data-split-pane-group="${groupId}"]`);

    // Get all panels using data attributes
    const panels = document.querySelectorAll<HTMLElement>(
      `[data-panel-group="${groupId}"][data-panel-id]`,
    );

    // Read actual computed sizes from all DOM elements
    const allSizes: Record<string, number> = {};

    if (container && panels.length > 0) {
      const containerSize =
        direction === "horizontal" ? container.clientWidth : container.clientHeight;

      if (containerSize > 0) {
        panels.forEach((panel) => {
          const panelId = panel.getAttribute("data-panel-id");
          if (panelId) {
            const panelSize = direction === "horizontal" ? panel.offsetWidth : panel.offsetHeight;
            const percentage = (panelSize / containerSize) * 100;
            allSizes[panelId] = percentage;
          }
        });
      }
    }

    // Fallback: use the provided sizes for the two affected panels if we couldn't read from DOM
    if (Object.keys(allSizes).length === 0) {
      if (paneNumber > 0 && panels[paneNumber - 1]) {
        const prevPanelId = panels[paneNumber - 1].getAttribute("data-panel-id");
        if (prevPanelId) {
          allSizes[prevPanelId] = preSize;
        }
      }
      if (paneNumber < panels.length && panels[paneNumber]) {
        const nextPanelId = panels[paneNumber].getAttribute("data-panel-id");
        if (nextPanelId) {
          allSizes[nextPanelId] = nextSize;
        }
      }
    }

    setPanelSizes((sizes) => {
      const newSizes = { ...sizes, ...allSizes };

      // Handle persistence
      if (persistCookie) {
        const state: SplitPanePersistentState = {
          panels: Object.entries(newSizes).map(([id, size], index) => ({
            id,
            size,
            index,
          })),
        };

        setCookie(persistCookie.name, btoa(JSON.stringify(state)), {
          expiresMs: persistCookie.expirationMs || COOKIE_NEVER_EXPIRES * 1000,
        });
      }
      return newSizes;
    });
  };

  const [dragging, setDragging] = useState(false);
  const handleDragStart = () => {
    setDragging(true);
  };

  return (
    <SplitPaneContext.Provider
      value={{
        groupId,
        direction,
        defaultState: defaultLayout,
        panelSizes,
      }}
    >
      <Split
        visible
        data-dragging={dragging}
        data-split-pane-group={groupId}
        mode={direction}
        onDragging={handleDragStart}
        className={className}
        onDragEnd={handleDragEnd}
      >
        {childrenArray}
      </Split>
    </SplitPaneContext.Provider>
  );
};

SplitPane.Panel = SplitPanePanel;

export default SplitPane;
